import { useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { CastCard } from "./CastCard";
import { useLoras } from "../../hooks/loras";
import type { Lora } from "../../types/loras";

interface CastGridProps {
  projectId: string;
  /** Optional callback for cast clicks - if provided, uses callback instead of navigation */
  onCastClick?: (castId: string) => void;
  /** Callback to report loading state to parent */
  onLoadingChange?: (isLoading: boolean) => void;
}

interface DisplayCast {
  id: string;
  name: string;
  description: string;
  imageUrl: string | null;
  thumbnailAssetId: string | null;
}

function loraToDisplayCast(lora: Lora): DisplayCast {
  return {
    id: lora.id,
    name: lora.name,
    description: lora.description ?? "",
    imageUrl: null,
    thumbnailAssetId: lora.thumbnailAssetId ?? null,
  };
}

export function CastGrid({
  projectId,
  onCastClick,
  onLoadingChange,
}: CastGridProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const lorasQuery = useLoras();

  // Report loading state to parent
  useEffect(() => {
    onLoadingChange?.(lorasQuery.isLoading);
  }, [lorasQuery.isLoading, onLoadingChange]);

  const handleCastClick = (castId: string) => {
    if (onCastClick) {
      // Use callback if provided (for slide transition mode)
      onCastClick(castId);
    } else {
      // Otherwise navigate to separate route
      navigate(`/productions/${projectId}/cast/${castId}`);
    }
  };

  // Cast = characters only. Filter to subject LoRAs (characters) and exclude archived.
  const displayCasts = useMemo<DisplayCast[]>(() => {
    const loras = lorasQuery.data ?? [];
    return loras
      .filter(
        (lora) => lora.statusSlug !== "archived" && lora.kindSlug === "subject",
      )
      .map(loraToDisplayCast);
  }, [lorasQuery.data]);

  const isLoading = lorasQuery.isLoading;

  const handleNewCastClick = () => {
    searchParams.set("modal", "create-cast");
    setSearchParams(searchParams);
  };

  // Parent handles loading display via onLoadingChange callback
  if (isLoading) {
    return null;
  }

  return (
    <div
      className="grid gap-6"
      style={{
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 240px))",
      }}
    >
      {/* Create new cast card - always first */}
      <div
        key="new-cast"
        className="card-stagger-fade"
        style={{ animationDelay: "0ms" }}
      >
        <div
          className="group cursor-pointer"
          style={{ maxWidth: "240px" }}
          onClick={handleNewCastClick}
        >
          <div className="w-full aspect-[3/4] rounded-[8px] overflow-hidden transition-all duration-200 flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/20 hover:border-white/40 hover:bg-white/5">
            <div className="w-10 h-10 rounded-full border-2 border-dashed border-white/30 flex items-center justify-center group-hover:border-white/50 transition-colors">
              <Plus className="w-5 h-5 text-white/40 group-hover:text-white/60 transition-colors" />
            </div>
            <span className="text-sm text-white/40 group-hover:text-white/60 transition-colors font-medium">
              New character
            </span>
          </div>
        </div>
      </div>

      {/* Cast cards with staggered fade animation */}
      {displayCasts.map((cast, index) => (
        <div
          key={cast.id}
          className="card-stagger-fade"
          style={{ animationDelay: `${(index + 1) * 50}ms` }}
        >
          <CastCard
            id={cast.id}
            name={cast.name}
            type="character"
            description={cast.description}
            imageUrl={cast.imageUrl}
            thumbnailAssetId={cast.thumbnailAssetId}
            onClick={() => handleCastClick(cast.id)}
          />
        </div>
      ))}
    </div>
  );
}
